// Spinning the town's webs (Sam, 31 Oct: "if you can think of a way to do procedural webs everywhere that would be
// cool").
//
// Where a web goes was found in Blender against the real town (scripts/blender/halloween.py): a point stepped off a
// wall, a lamp, a fence or a bin, twelve threads cast in a plane through it, and kept where three or more caught on
// something and between them went more than half way round. That leaves a hub and the anchors its threads reached,
// in public/models/halloween-webs.json. This spins a web in each frame the way an orb weaver does: the frame threads
// from anchor to anchor, the radials out from the hub, then the sticky spiral wound from the inside out, sagging a
// little between radials. Each web is seeded by where it is, so it is the same web on every visit, and old webs have
// gaps in the spiral and a radial or two gone.
//
// Out: line segment pairs (positions), and for each vertex how free it is to move (0 on an anchor, most at the
// middle of a thread) and the web's own phase, for the wind.

function rngFor(x, y, z) {
  let s = (Math.imul(Math.round(x * 100), 73856093) ^ Math.imul(Math.round(y * 100), 19349663) ^ Math.imul(Math.round(z * 100), 83492791)) >>> 0
  return () => {
    s = (s + 0x6d2b79f5) >>> 0
    let t = s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
const add = (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
const mul = (a, k) => [a[0] * k, a[1] * k, a[2] * k]
const lerp = (a, b, t) => add(a, mul(sub(b, a), t))
const len = (a) => Math.hypot(a[0], a[1], a[2])

/**
 * One web's threads. frame: { h: hub [x, y, z], a: anchors in order round the hub }.
 * Returns { segs: [[p, q, freeP, freeQ], ...] } with points in the town's coordinates.
 */
export function spinWeb(frame, { tattered = 0.14 } = {}) {
  const hub = frame.h
  const anchors = frame.a || []
  if (anchors.length < 3) return { segs: [], phase: 0 }
  const rnd = rngFor(hub[0], hub[1], hub[2])
  const segs = []

  // the frame: anchor to anchor, all the way round (the open side of a corner web is bridged, as a spider bridges it)
  const rim = []
  for (let i = 0; i < anchors.length; i++) {
    const a = anchors[i]
    const b = anchors[(i + 1) % anchors.length]
    segs.push([a, b, 0, 0])
    rim.push(a)
    // radials also land part way along each frame thread, about one every 60 cm, so the spokes are evenly spread
    // whatever the anchors were
    const extra = Math.min(3, Math.max(0, Math.round(len(sub(b, a)) / 0.6) - 1))
    for (let k = 1; k <= extra; k++) rim.push(lerp(a, b, k / (extra + 1)))
  }

  // the radials, hub to rim. a few are gone on an old web
  const spokes = rim.filter(() => rnd() > tattered * 0.5)
  for (const r of spokes) segs.push([hub, r, 0.6, 0.15])

  // the spiral, wound out from near the hub to near the frame, one ring a turn, sagging between spokes. a bigger web
  // has more turns. across the open side of a corner web (two spokes far apart round the hub) the spiral does not
  // cross: it turns back, as it does against a wall
  const n = spokes.length
  const reach = spokes.reduce((m, r) => m + len(sub(r, hub)), 0) / n
  const rings = Math.min(11, Math.max(4, Math.round(reach * 3.5)))
  const steps = n * rings
  const dir = spokes.map((r) => { const d = sub(r, hub); return mul(d, 1 / (len(d) || 1)) })
  const apart = (i, j) => dir[i][0] * dir[j][0] + dir[i][1] * dir[j][1] + dir[i][2] * dir[j][2] < -0.17
  const down = [0, -1, 0]
  let prev = null
  for (let s = 0; s <= steps; s++) {
    const i = s % n
    const t = 0.12 + 0.8 * (s / steps)
    const p = lerp(hub, spokes[i], t)
    if (prev && !apart((i + n - 1) % n, i) && rnd() > tattered) {
      const mid = lerp(prev, p, 0.5)
      const sag = add(mid, mul(down, len(sub(p, prev)) * 0.05))
      segs.push([prev, sag, 0.8, 1.0], [sag, p, 1.0, 0.8])
    }
    prev = p
  }
  return { segs, phase: rnd() * Math.PI * 2 }
}

/** Every web, packed for one line-segments draw: positions, free (0..1) and phase per vertex. */
export function spinWebs(frames, opts) {
  const pos = []
  const free = []
  const phase = []
  for (const f of frames || []) {
    const { segs, phase: ph } = spinWeb(f, opts)
    for (const [p, q, fp, fq] of segs) {
      pos.push(p[0], p[1], p[2], q[0], q[1], q[2])
      free.push(fp, fq)
      phase.push(ph, ph)
    }
  }
  return { positions: new Float32Array(pos), free: new Float32Array(free), phase: new Float32Array(phase) }
}
